import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useLocation } from "react-router-dom";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, Typography, } from "@mui/material";
import { useAuth, getCookie, getAxios } from "@coremind/identity-core";
import { SignInPage } from "@coremind/identity-ui";
/**
 * Login and consent pages for Ory Hydra, the OAuth/OIDC server behind this host (the iOS app, MCP
 * connectors and, after its migration, Matrix's MAS all sign in through here).
 *
 * Hydra redirects the browser to /auth/hydra/login?login_challenge=... and later to
 * /auth/hydra/consent?consent_challenge=.... These pages never decide anything themselves: login signs
 * the user in with Cognito exactly like /auth/signin, then asks coremind-api (POST /auth/hydra/*, which
 * can reach Hydra's cluster-internal admin API) to accept the challenge and returns the browser to
 * Hydra. See apps/coremind/api/app/v1/routes/auth_hydra.py.
 */
// Hydra v2 challenges are ~1.2KB base64url blobs with `=` padding (measured against the live server);
// the original 200-character limit rejected every real challenge.
const CHALLENGE_RE = /^[A-Za-z0-9_.=\-]{8,4096}$/;
/** A Google sign-in navigates away and returns without this page's query string, so remember the
 * challenge across that round trip (the /auth/signin route picks it up again). */
export const PENDING_HYDRA_LOGIN_STORAGE_KEY = "coremind:pending-hydra-login";
/** An abandoned sign-in must not hijack a later, unrelated one in the same tab: the remembered
 * challenge is only honoured for a few minutes (Hydra's own challenges also expire). */
const PENDING_TTL_MS = 10 * 60 * 1000;
export function rememberHydraLogin(challenge) {
    window.sessionStorage.setItem(PENDING_HYDRA_LOGIN_STORAGE_KEY, JSON.stringify({ challenge, at: Date.now() }));
}
/** Returns (and clears) the remembered challenge if it is recent and well formed, else null. */
export function takePendingHydraLogin() {
    const raw = window.sessionStorage.getItem(PENDING_HYDRA_LOGIN_STORAGE_KEY);
    if (!raw)
        return null;
    window.sessionStorage.removeItem(PENDING_HYDRA_LOGIN_STORAGE_KEY);
    try {
        const { challenge, at } = JSON.parse(raw);
        if (typeof challenge === "string" && CHALLENGE_RE.test(challenge) && typeof at === "number" && Date.now() - at < PENDING_TTL_MS) {
            return challenge;
        }
    }
    catch {
        /* fall through */
    }
    return null;
}
function Message({ children }) {
    return (_jsx("div", { style: { minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "sans-serif", textAlign: "center" }, children: _jsx("p", { style: { maxWidth: 420 }, children: children }) }));
}
/** The API's redirect_to always points back into Hydra (or the client's own callback); refuse anything
 * that is not https on this platform's domain before navigating the browser there. */
export function isTrustedRedirect(value) {
    if (typeof value !== "string")
        return false;
    try {
        const url = new URL(value);
        return url.protocol === "https:" && (url.hostname === "offsetmind.com" || url.hostname.endsWith(".offsetmind.com"));
    }
    catch {
        return false;
    }
}
function errorText(err) {
    const detail = err?.response?.data?.detail;
    if (typeof detail === "string" && detail)
        return detail;
    return "We could not complete this sign-in. Return to the app and try again.";
}
async function completeAndRedirect(path, body) {
    const { data } = await getAxios().post(path, body);
    if (!isTrustedRedirect(data?.redirect_to)) {
        throw new Error("Unexpected response from the sign-in service");
    }
    window.location.replace(data.redirect_to);
}
/** The Cognito session cookie is written just after the session resolves; wait briefly for it. */
async function readIdToken() {
    for (let i = 0; i < 10; i += 1) {
        const token = getCookie("idToken");
        if (token)
            return token;
        await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return null;
}
export function HydraLoginRoute() {
    const { session, loading } = useAuth();
    const location = useLocation();
    const challenge = new URLSearchParams(location.search).get("login_challenge") ?? "";
    const valid = CHALLENGE_RE.test(challenge);
    const [error, setError] = React.useState(null);
    const started = React.useRef(false);
    React.useEffect(() => {
        if (valid && !loading && !session) {
            rememberHydraLogin(challenge);
        }
    }, [valid, loading, session, challenge]);
    React.useEffect(() => {
        if (!valid || loading || !session || started.current)
            return;
        started.current = true;
        window.sessionStorage.removeItem(PENDING_HYDRA_LOGIN_STORAGE_KEY);
        (async () => {
            try {
                const idToken = await readIdToken();
                if (!idToken)
                    throw new Error("No ID token for the signed-in session");
                await completeAndRedirect("/auth/hydra/login/accept", { login_challenge: challenge, id_token: idToken });
            }
            catch (err) {
                console.error("Hydra login accept failed:", err);
                setError(errorText(err));
            }
        })();
    }, [valid, loading, session, challenge]);
    if (!valid)
        return _jsx(Message, { children: "This sign-in link is missing or invalid. Return to the app and try again." });
    if (error)
        return _jsx(Message, { children: error });
    if (loading || session)
        return _jsx(Message, { children: "Signing you in\u2026" });
    return _jsx(SignInPage, { showSignupPrompt: true });
}
/** Plain-language label for a scope shown on the consent screen; unknown scopes fall back to the
 * raw string rather than being hidden, so nothing is granted without the user seeing it. */
const SCOPE_LABELS = {
    openid: "Confirm who you are",
    email: "Your email address",
    profile: "Your name",
    "offsetmind.mcp": "Connect as an AI assistant",
    "offsetmind.read": "Read your Offsetmind data",
};
async function postConsent(challenge, body) {
    const { data } = await getAxios().post("/auth/hydra/consent/accept", { consent_challenge: challenge, ...body });
    if (!isTrustedRedirect(data?.redirect_to)) {
        throw new Error("Unexpected response from the sign-in service");
    }
    window.location.replace(data.redirect_to);
}
// Same branded-card treatment as SignIn.tsx (the one other screen on this host users see while
// signed out), so an OAuth consent screen still reads as part of the Offsetmind product rather
// than a generic identity-provider page.
const cardSx = {
    borderRadius: 5,
    overflow: "hidden",
    border: "1px solid rgba(124, 145, 186, 0.24)",
    background: "radial-gradient(circle at top left, rgba(196, 223, 255, 0.22), transparent 32%), linear-gradient(120deg, rgba(255, 148, 77, 0.22), transparent 34%), #f7f4ee",
    boxShadow: "0 32px 90px rgba(0, 0, 0, 0.28)",
    maxWidth: 480,
    width: "100%",
};
const panelSx = {
    backgroundColor: "rgba(255,255,255,0.93)",
    borderRadius: 4,
    p: { xs: 3, md: 4 },
    color: "#2f3954",
    backdropFilter: "blur(12px)",
};
/** The real approval screen for trusted (non-first-party) connectors: shows who is asking, what for,
 * and which site(s) they'd get access to, and only grants anything once the user explicitly approves. */
function TrustedConnectorConsent({ challenge, info }) {
    const [sites, setSites] = React.useState(() => new Set(info.offerable_sites));
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    const toggleSite = (site) => {
        setSites((prev) => {
            const next = new Set(prev);
            if (next.has(site))
                next.delete(site);
            else
                next.add(site);
            return next;
        });
    };
    const respond = async (approved) => {
        setBusy(true);
        setError(null);
        try {
            await postConsent(challenge, { approved, approved_sites: approved ? Array.from(sites) : [] });
        }
        catch (err) {
            console.error("Hydra consent accept failed:", err);
            setError(errorText(err));
            setBusy(false);
        }
    };
    return (_jsx(Box, { sx: { minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }, children: _jsx(Box, { sx: cardSx, children: _jsx(Box, { sx: panelSx, children: _jsxs(Stack, { spacing: 3, children: [_jsxs(Stack, { spacing: 1.5, alignItems: "flex-start", children: [_jsx(Box, { component: "img", src: "images/logo.png", alt: "Offsetmind", sx: { height: 36, width: "auto" } }), _jsxs(Typography, { variant: "h2", sx: { fontSize: { xs: 22, md: 26 }, fontWeight: 700 }, children: [info.client_name, " wants to access your Offsetmind account"] }), _jsxs(Typography, { variant: "body1", sx: { color: "#5a6480" }, children: ["This lets ", info.client_name, " act on your behalf, limited to what you approve below."] })] }), _jsx(Stack, { spacing: 1, children: info.requested_scope.map((scope) => (_jsxs(Typography, { variant: "body2", sx: { color: "#4b5675" }, children: ["\u2022 ", SCOPE_LABELS[scope] ?? scope] }, scope))) }), info.offerable_site_details.length > 0 && (_jsxs(Box, { sx: {
                                borderRadius: 3,
                                px: 2.5,
                                py: 2,
                                backgroundColor: "rgba(236, 239, 248, 0.88)",
                            }, children: [_jsx(Typography, { variant: "body1", sx: { fontWeight: 600, mb: 0.5 }, children: "Sites to share" }), _jsx(Stack, { spacing: 0.5, children: info.offerable_site_details.map((site) => (_jsx(FormControlLabel, { sx: { alignItems: "flex-start", ml: 0 }, control: _jsx(Checkbox, { checked: sites.has(site.slug), onChange: () => toggleSite(site.slug), sx: { pt: 0.25 } }), label: _jsxs(Box, { children: [_jsx(Typography, { variant: "body1", sx: { fontWeight: 600, color: "#2f3954" }, children: site.label }), site.blurb && (_jsx(Typography, { variant: "body2", sx: { color: "#5a6480" }, children: site.blurb }))] }) }, site.slug))) })] })), error && _jsx(Alert, { severity: "error", children: error }), _jsxs(Stack, { direction: "row", spacing: 1.5, children: [_jsx(Button, { type: "button", variant: "contained", size: "large", fullWidth: true, disabled: busy, onClick: () => respond(true), startIcon: busy ? _jsx(CircularProgress, { size: 16, color: "inherit" }) : undefined, children: "Approve" }), _jsx(Button, { type: "button", variant: "outlined", size: "large", fullWidth: true, disabled: busy, onClick: () => respond(false), children: "Deny" })] })] }) }) }) }));
}
export function HydraConsentRoute() {
    const location = useLocation();
    const challenge = new URLSearchParams(location.search).get("consent_challenge") ?? "";
    const valid = CHALLENGE_RE.test(challenge);
    const [error, setError] = React.useState(null);
    const [info, setInfo] = React.useState(null);
    const started = React.useRef(false);
    React.useEffect(() => {
        if (!valid || started.current)
            return;
        started.current = true;
        (async () => {
            try {
                const { data } = await getAxios().get(`/auth/hydra/consent/${encodeURIComponent(challenge)}`);
                if (data.trusted_connector) {
                    // Show a real approval screen; the user's choice drives the POST, not an automatic accept.
                    setInfo(data);
                    return;
                }
                // First-party clients are approved automatically by the API; any other non-trusted client
                // comes back as an error redirect to the client (access_denied).
                await completeAndRedirect("/auth/hydra/consent/accept", { consent_challenge: challenge });
            }
            catch (err) {
                console.error("Hydra consent failed:", err);
                setError(errorText(err));
            }
        })();
    }, [valid, challenge]);
    if (!valid)
        return _jsx(Message, { children: "This approval link is missing or invalid. Return to the app and try again." });
    if (error)
        return _jsx(Message, { children: error });
    if (info)
        return _jsx(TrustedConnectorConsent, { challenge: challenge, info: info });
    return _jsx(Message, { children: "Finishing sign-in\u2026" });
}
